// Utility for validating and extracting safe links from AI responses
// Only allows links that exist in the ai_knowledge_base

const WHITELISTED_PATTERNS = [
  'https://www.kalendes.com/site/biobeautezen/welcome',
  'biobeautezen.institut@gmail.com',
  '04 99 53 83 08'
];

export interface ParsedLink {
  text: string;
  href: string;
  type: 'url' | 'email' | 'phone';
}

// Extract and validate links from text content
export const parseAndValidateLinks = (text: string): string => {
  // Markdown link pattern [text](url)
  const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s<>"{}|\\^`[\]]+)\)/gi;
  // URL pattern (for standalone URLs)
  const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  // Email pattern  
  const emailPattern = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;

  let processedText = text;

  // First, process markdown links to avoid duplication
  processedText = processedText.replace(markdownLinkPattern, (match, linkText, url) => {
    if (isLinkWhitelisted(url)) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="link-external">${linkText}</a>`;
    }
    return match; // Keep original markdown if not whitelisted
  });

  // Then process remaining standalone URLs (not already in markdown)
  processedText = processedText.replace(urlPattern, (match) => {
    // Skip if this URL is already part of an HTML link
    if (processedText.includes(`href="${match}"`)) {
      return match;
    }
    if (isLinkWhitelisted(match)) {
      return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="link-external">${match}</a>`;
    }
    return match; // Keep original text if not whitelisted
  });

  // Process emails
  processedText = processedText.replace(emailPattern, (match) => {
    if (isLinkWhitelisted(match)) {
      return `<a href="mailto:${match}" class="link-email">${match}</a>`;
    }
    return match;
  });

  return processedText;
};

// Check if a link is in our whitelist
const isLinkWhitelisted = (link: string): boolean => {
  return WHITELISTED_PATTERNS.some(pattern => {
    // Exact match for security
    return link === pattern || link.includes(pattern);
  });
};

// Extract all potential links for validation
export const extractLinks = (text: string): string[] => {
  const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
  const emailPattern = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;

  const urls = text.match(urlPattern) || [];
  const emails = text.match(emailPattern) || [];

  return [...urls, ...emails];
};